import Clipboard from "clipboard";
import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";
import Collapse from "@kunukn/react-collapse";
import classnames from "classnames";
import JSONbig from "json-bigint";
import { ElementType } from "../processEditor/model/IProcessData";
import { Utilities } from "../shared/utilities";

import {
    ParameterValueDisplayPreformatted,
    ParameterValueNonFormatted,
    ParameterViewerProps,
    ShowRawOrFormatted,
    ExpandableParameterDisplayProps,
    LabelDisplayProps,
    ParameterDisplayProps,
    RecursiveParameterDisplayProps,
    JsonDisplayType,
    BigNumber,
    ParameterValue
} from "./model/ParameterViewer";
import Time from "shared/Time";
import { LoopParameter } from "./Models";
import { HashMap } from "shared/model/HashMap";
import SearchContainer from "./SearchContainer";

const clipboard = new Clipboard(".copy-to-clipboard-on-click");
clipboard.on("success", () => {
    Utilities.toastSuccess(FRENDS.Localizations.Flash_CopySuccess);
});

const ValueDisplay: React.FC<ParameterDisplayProps> = (props) => {

    const [isHovered, setHover] = React.useState<boolean>(false);

    const valueElement = <ParameterValueDisplayPreformatted className="parameter-value-display-preformatted-selector" title={props.title}>{props.value}</ParameterValueDisplayPreformatted>;
    if (!props.label) {
        return valueElement;
    }

    let copyLabelDotNotation, copyLabelValue;
    if (props.isResult && props.parentPropertyName) {
        copyLabelValue = <CopyToClipboard value={props.value} id="CopyLabelValue" />;
        copyLabelDotNotation = <CopyDotNotationToClipboard value={props.parentPropertyName + "" + props.label} id="CopyLabelDotNotation" />;
    }
    return (
        <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
            <S.FormGroup>
                <LabelDisplay
                    label={props.label}
                    isHovered={isHovered}
                    isResult={props.isResult}
                    copyLabelDotNotation={copyLabelDotNotation}
                    copyLabelValue={copyLabelValue} />
                {valueElement}
            </S.FormGroup>
        </div>
    );
};

const LabelDisplay: React.FC<LabelDisplayProps> = (props) => {
    return (
        <div className="parameter-label-selector">
            <S.Label className="parameter-label-value-selector">
                {props.label}
            </S.Label>
            &nbsp;
            {props.isResult && props.isHovered ? props.copyLabelValue : null}
            &nbsp;
            {props.isResult && props.isHovered ? props.copyLabelDotNotation : null}
        </div>
    );
};

const ExpandableParameterDisplay: React.FC<React.PropsWithChildren<ExpandableParameterDisplayProps>> = (props) => {
    const [state, setState] = React.useState<{ expanded?: boolean, mouseOver?: boolean }>({
        expanded: false,
        mouseOver: false
    });

    let copyToClipboardIcon, copyDotNotationPath = null;
    if (state.mouseOver) {
        copyToClipboardIcon = <CopyToClipboard value={props.rawVal} id="CopyResult" />;
        if (props.isResult) {
            copyDotNotationPath = <CopyDotNotationToClipboard value={props.propertyPath} id="CopyDotNotation" />;
        }
    }

    const handleMouseEnter = () => setState({ ...state, mouseOver: true });
    const handleMouseLeave = () => setState({ ...state, mouseOver: false });
    const handleLabelClick = () => setState({ ...state, expanded: !state.expanded });

    if (props.label) {
        const collapseIconClassName = classnames({
            "ci-chevron_big_up": state.expanded,
            "ci-chevron_big_down": !state.expanded
        } as ClassValue);
        return (
            <S.ElementRowStyled
                $level={props.level}
                variant={state.expanded ? S.ElementRowVariant.selected : S.ElementRowVariant.primary}
                onMouseEnter={() => handleMouseEnter()}
                onMouseLeave={() => handleMouseLeave()}
            >
                <S.ElementHeaderContainer className="expandable-parameter-label-selector">
                    <S.Label onClick={() => handleLabelClick()} className="pointer expandable-parameter-label-value-selector">
                        <i className={collapseIconClassName} aria-hidden="true"></i> {props.label}
                        &nbsp;
                        {copyToClipboardIcon}
                        &nbsp;
                        {copyDotNotationPath}
                    </S.Label>
                </S.ElementHeaderContainer>
                <S.ElementContentContainer className="expandable-parameter-value-selector">
                    <Collapse isOpen={state.expanded}>
                        <ParameterValueDisplayPreformatted className="parameter-value-display-preformatted-selector">
                            {props.children}
                        </ParameterValueDisplayPreformatted>
                    </Collapse>
                </S.ElementContentContainer>
            </S.ElementRowStyled>
        );
    } else {
        return (
            <div>
                {props.children}
            </div>
        );
    }
};

function isCancellationTokenParameter(value: unknown) {
    if (!_.isObject) {
        return false;
    }
    return _.has(value, "CanBeCanceled") && _.has(value, "IsCancellationRequested");
}
function isException(value: unknown) {
    return (_.isObject(value) && _.has(value, "Message") && _.has(value, "StackTraceString") && _.has(value, "InnerException"));
}

function tryParseJson(value: string) {
    try {
        value = JSONbig.parse(value);
    } catch (exception) {
        // Do nothing, continue with the unparsed value
    }
    return value;
}

export const RecursiveParameterDisplay: React.FC<RecursiveParameterDisplayProps> = (props) => {
    if (isCancellationTokenParameter(props.value)) {
        return null;
    }
    if (_.isBoolean(props.value)) {
        return <ValueDisplay value={JSON.stringify(props.value)} key={props.parameterId} parentPropertyName={props.parentPropertyName} label={props.label} isResult={props.isResult} />;
    }

    if (props.value === null || props.value === undefined) {
        return <ValueDisplay value={JSON.stringify(props.value, null, "\t")} key={props.parameterId} parentPropertyName={props.parentPropertyName} label={props.label} isResult={props.isResult} />;
    }

    const level = props.parentPropertyName !== null && props.level ? props.level + 1 : 1;
    const decideParentPropertyNamePattern = (pattern: string): string | null => {
        let parentPropertyName: string | null = null;
        if (props.label != null) {
            parentPropertyName = props.parentPropertyName != null ? props.parentPropertyName + "" + props.label + pattern : props.label + pattern;
        } else if (props.parentPropertyName != null) {
            parentPropertyName = props.parentPropertyName + pattern;
        }
        return parentPropertyName;
    };

    let val = props.value;

    // if it's a string that seems to contain a json object or an array, attempt to parse it:
    if (_.isString(val) && (val.trim().startsWith("{") || val.trim().startsWith("["))) {
        val = tryParseJson(val);
    }
    if (_.isArray(val)) {
        return (
            <ExpandableParameterDisplay level={level} propertyPath={props.parentPropertyName != null ? props.parentPropertyName + "" + props.label : props.label} label={props.label} isResult={props.isResult} rawVal={JSONbig.stringify(val, null, "\t")}>
                <table style={{ width: "100%" }}>
                    <tbody>
                        {val.map((v, index) => {
                            const parentPropertyName = decideParentPropertyNamePattern(`[${index}]`);
                            return (
                                // level + 1 here so that we can differentiate object inside array easier
                                <S.ElementArrayObject $level={level + 1} key={props.parameterId as string + index}>
                                    <td>
                                        <RecursiveParameterDisplay level={level} isResult={props.isResult} parentPropertyName={parentPropertyName} value={v} parameterId={props.parameterId} />
                                    </td>
                                </S.ElementArrayObject>
                            )
                        }
                        )}
                    </tbody>
                </table>
            </ExpandableParameterDisplay>
        );
    }

    if (_.isObject(val) && (val as BigNumber)._isBigNumber) {
        val = (val as BigNumber).toFixed();// displays the whole value as a string
    }

    let keys = Object.keys(val);
    if (_.isObject(val) && keys.length !== 0) {
        if (isException(val)) {
            // pick only given fields from an exception
            keys = ["ClassName", "Message", "StackTraceString", "InnerException"];
        }
        const parentPropertyName = decideParentPropertyNamePattern(".");
        return <ExpandableParameterDisplay level={level} propertyPath={props.parentPropertyName != null ? props.parentPropertyName + "" + props.label : props.label} label={props.label} isResult={props.isResult} rawVal={JSON.stringify(val, null, "\t")}>
            {keys.map((x) => {
                const paramId = props.parameterId + "." + x;
                const label = !props.isResult ? Utilities.humanizeLabel(x) : x;
                const value = (val as Record<string, ParameterValue>)[x];
                return (
                    <RecursiveParameterDisplay level={level} key={x} parameterId={paramId} parentPropertyName={parentPropertyName} isResult={props.isResult} value={value} label={label} />
                );
            })}
        </ExpandableParameterDisplay>;
    }

    if (val === "") {
        val = "<< empty >>";
    }
    if (_.isObject(val)) {
        val = JSONbig.stringify(val, null, "\t");
    }

    return (
        <ValueDisplay
            value={val}
            key={props.parameterId}
            label={props.label}
            isResult={props.isResult}
            parentPropertyName={props.parentPropertyName}
        />
    )
};

export const ElementDisplayHeader: React.FC<{ name: string | undefined | null, state: string | undefined, duration: string | undefined, startTime: string | undefined, endTime: string | undefined }> = (props) => {
    let duration = "";
    let durationTitle = "";

    if (props.duration) {
        duration = Time.getDurationFromTimeSpan(props.duration);
        durationTitle = props.duration;
    } else if (props.endTime && props.startTime) {
        duration = Time.getDurationBetweenDates(props.startTime, props.endTime, true)
        durationTitle = Time.getDetailedDurationBetweenDates(props.startTime, props.endTime);
    }

    const stateIcon = Utilities.getIconFromExecutionState(props.state);

    return (
        <>
            <S.FormGroup>
                <S.FlexContainer centerItems={true}>
                    <span className={`task-state-${props.state}`}>{stateIcon}</span>&nbsp;
                    <h2>{props.name}</h2>
                </S.FlexContainer>
            </S.FormGroup>
            <ValueDisplay
                value={Time.toDetailedLocalTime(props.startTime)}
                key="startTime"
                isResult={false}
                label={FRENDS.Localizations.StartTime}
                title={"UTC: " + props.startTime}
            />
            {duration
                ? <ValueDisplay
                    value={duration}
                    key="duration"
                    isResult={false}
                    label={FRENDS.Localizations.Duration}
                    title={durationTitle}
                />
                : null}
        </>
    );
};

const LoopDisplayHeader: React.FC<{ currentIndex: number | null | undefined, maxLoopIndex: number | null | undefined, onChangeIndex: (newValue: number) => void }> = (props) => {
    function capCurrentIndex(value: number | null | undefined) {
        const maxValue = props.maxLoopIndex;
        if (value === null || value === undefined || isNaN(value)) {
            // tslint:disable-next-line:no-console
            console.error(`Invalid loop index value ${value}, will default to 1`);
            value = 1;
        }

        if (maxValue && value > maxValue) {
            value = maxValue;
        } else if (value < 1) {
            value = 1;
        }

        return value;
    }

    function handleChangeIndex(event: React.FormEvent<HTMLInputElement>) {
        const value = capCurrentIndex(Number(event.currentTarget.value));
        props.onChangeIndex(value! - 1);
    }

    const cappedIndex = capCurrentIndex(props.currentIndex && props.currentIndex + 1)!;
    return (
        <div>
            <h3>{FRENDS.Localizations.Iteration} {cappedIndex.toString()} {FRENDS.Localizations.Result.toLowerCase()}</h3>
            <S.FormGroup>
                <S.Label>
                    {FRENDS.Localizations.Iteration}
                </S.Label>
                <S.Input
                    isFullWidth={true}
                    key="changeIterationInput"
                    min="1"
                    max={props.maxLoopIndex as number}
                    type="number"
                    value={cappedIndex.toString()}
                    onChange={handleChangeIndex}
                />
            </S.FormGroup>
        </div>
    );
};

const WhileLoopDisplay: React.FC<{ id: string | undefined | null, expression: string, result: string, loopResults: Record<string, string> }> = (props) => {
    return (
        <div>
            <OptionalJsonDisplay jsonDisplayType={JsonDisplayType.Result} result={props.result} />
            <hr />
            <h3>{FRENDS.Localizations.Parameters}</h3>
            <ExpressionResultDisplay id={props.id} expressionResults={props.loopResults} parameterExpression={props.expression} />
        </div>
    );
};

const ExpressionResultDisplay: React.FC<{ id: string | undefined | null, expressionResults: Record<string, string>, parameterExpression?: string }> = (props) => {
    let resultDisplay = null;
    if (props.expressionResults) {
        resultDisplay = Object.keys(props.expressionResults).map((key) => {
            const name = Utilities.humanizeLabel(key);
            if (key === "evaluatedExpression" || key === "expression") {
                return <ValueDisplay value={props.expressionResults[key]} key={key} isResult={false} label={name} />;
            } else if (key === "expressionResult") {
                return <ValueDisplay value={JSON.stringify(props.expressionResults[key], null, "\t")} isResult={false} key={key} label={name} />;
            } else {
                return <ValueDisplay value={JSON.stringify(props.expressionResults[key], null, "\t")} isResult={false} key={key} label={key} />;
            }
        });
    }

    return <div>
        {props.parameterExpression
            ? <ValueDisplay value={props.parameterExpression} isResult={false} label={FRENDS.Localizations.Expression} key={props.id + "_expression"} />
            : null}
        {resultDisplay}
    </div>;
};

export const CopyToClipboard: React.FC<{ id: string, value: ParameterValue }> = (props) => (
    <i
        className="ci-copy pointer copy-to-clipboard-on-click"
        title="Copy value to clipboard"
        id={props.id}
        key={props.id}
        data-clipboard-text={props.value}
    />
);

const CopyDotNotationToClipboard: React.FC<{ id: string, value: string | undefined }> = (props) => {
    return (
        <i
            className="ci-path copy-to-clipboard-on-click pointer"
            title="Copy dot notation path to clipboard"
            data-clipboard-text={props.value}
            id={props.id}
            key={props.id}
        />
    )
};

export const OptionalJsonDisplay: React.FC<{ result: ParameterValue, jsonDisplayType: JsonDisplayType, parameterId?: string }> = (props) => {
    let label: string;
    let parameterId: string | undefined;

    switch (props.jsonDisplayType) {
        case JsonDisplayType.Result:
            label = FRENDS.Localizations.Result;
            parameterId = "result";
            break;
        case JsonDisplayType.Parameters:
            label = FRENDS.Localizations.Parameters;
            parameterId = props.parameterId;
            break;
        case JsonDisplayType.ProcessVariables:
            label = "Process variables";
            break;
    }

    const [state, setState] = React.useState<{ showAsJson: boolean }>({ showAsJson: false });

    const handleShowJsonChange = () => {
        setState({ showAsJson: !state.showAsJson });
    };

    if (props.result != null) {
        let jsonValue = props.result;
        if (state.showAsJson) {
            jsonValue = JSONbig.stringify(props.result, null, "\t");
        } else {
            if (_.isArray(props.result) || _.isObject(props.result)) {
                jsonValue = JSONbig.stringify(props.result, null, "\t");
            }
        }
        return (
            <div className="result-selector">
                <S.FormGroup>
                    <h3>{label}&nbsp;
                        {jsonValue ?
                            <>
                                <CopyToClipboard value={jsonValue} id="CopyResult" />
                                <br />
                                <ShowRawOrFormatted
                                    id="ChangeFormat"
                                    className="pointer"
                                    onClick={() => handleShowJsonChange()}
                                >
                                    {state.showAsJson ? FRENDS.Localizations.ShowFormatted : FRENDS.Localizations.ShowRaw}
                                </ShowRawOrFormatted>
                            </>
                            : null}
                    </h3>
                </S.FormGroup>
                {state.showAsJson ?
                    <ParameterValueNonFormatted>{jsonValue as string}</ParameterValueNonFormatted>
                    : <RecursiveParameterDisplay isResult={props.jsonDisplayType == JsonDisplayType.Result} parameterId={parameterId} value={props.result} />}
            </div>
        );
    } else {
        return null;
    }
}

const ForeachLoopDisplay: React.FC<{
    id: string | undefined | null,
    variableName: string,
    expression: string,
    variableValue: ParameterValue,
    loopResult: ParameterValue,
}> = (props) => {
    let variableValueDisplay = null;
    if (props.variableValue) {
        variableValueDisplay = <div>
            <LabelDisplay isResult={true} label={props.variableName + " " + FRENDS.Localizations.Value.toLowerCase()} />
            <RecursiveParameterDisplay
                isResult={true}
                value={props.variableValue}
                parameterId={props.id + "_varValue"}
            />
        </div>;
    }
    return (
        <div>
            <OptionalJsonDisplay jsonDisplayType={JsonDisplayType.Result} result={props.loopResult} />
            <>
                <hr />
                <h3>{FRENDS.Localizations.Parameters}</h3>
                <ValueDisplay value={props.expression} isResult={true} label={FRENDS.Localizations.Expression} key={props.id + "_expression"} />
                {variableValueDisplay ? variableValueDisplay : <ValueDisplay value={props.variableName} isResult={true} label={FRENDS.Localizations.Variable} key={props.id + "_variable"} />}
            </>
        </div>
    );
};

export const ParameterViewer: React.FC<ParameterViewerProps> = (props) => {

    if (props.id === null) {
        return <></>;
    }

    function getParameters() {
        if (props.step?.parameters != null) {
            const nonLoopParameters = _.pickBy(props.step.parameters, (value, key) => !key.startsWith("$loop"));
            if (Object.keys(nonLoopParameters).length > 0) {
                return <>
                    <hr />
                    <OptionalJsonDisplay result={nonLoopParameters} jsonDisplayType={JsonDisplayType.Parameters} parameterId="parameters" />
                </>;
            }
        }
        return null;
    }

    function getProcessVariables() {
        if (props.step?.processVariables?.length === 0) {
            return null;
        }

        return <>
            <hr />
            <OptionalJsonDisplay result={props.step?.processVariables} jsonDisplayType={JsonDisplayType.ProcessVariables} />
        </>
    }

    function handleChangeIndex(newIndex: number) {
        if (props.onLoopIndexChangeClick) {
            props.onLoopIndexChangeClick(props.step?.stepId, newIndex);
        }
    }


    const handleScopeSearch = (searchText: string) => {
        if (props.onSearch) {
            props.onSearch(searchText, props.step?.stepId);
        }
    }
    const handleMouseOverSearchResult = (stepId: string) => {
        if (props.onMouseOverSearchResult) {
            props.onMouseOverSearchResult(stepId);
        }
    }

    const handleSearchResultClicked = (stepId: string, loopParamaters: HashMap<LoopParameter>) => {
        if (props.onSearchResultClicked) {
            props.onSearchResultClicked(stepId, loopParamaters);
        }
    }
    function renderPanel() {
        switch (props.elementType) {
            case ElementType.While: {
                const loopResults = props.step?.result && props.step.result.loopResults;
                const result = props.step?.result && props.step.result.result;
                return <>
                    <LoopDisplayHeader
                        currentIndex={props.currentIndexes && props.currentIndexes[props.step?.stepId as string]}
                        maxLoopIndex={props.maxLoopIndex}
                        onChangeIndex={handleChangeIndex}

                    />
                    <SearchContainer onSearchResultClick={handleSearchResultClicked} onMouseOver={handleMouseOverSearchResult} searchState={props.searchState} onSearch={handleScopeSearch} />
                    <WhileLoopDisplay
                        id={props.id}
                        loopResults={loopResults ? props.currentIndexes && loopResults[props.currentIndexes[props.step?.stepId as string]] : null}
                        result={result}
                        expression={props.elementParameters?.parameters["expression"].value}
                    />
                </>;
            }
            case ElementType.ParallelForeach:
            case ElementType.SequentialForeach: {
                let variableValue = null;
                if (props.step?.parameters && props.step.parameters["list"]) {
                    variableValue = props.currentIndexes && props.step.parameters["list"][props.currentIndexes[props.step.stepId]];
                }
                const foreachLoopResults = props.step?.result && props.step.result.result;
                return <div>
                    <LoopDisplayHeader
                        currentIndex={props.currentIndexes && props.currentIndexes[props.step?.stepId as string]}
                        maxLoopIndex={props.maxLoopIndex}
                        onChangeIndex={handleChangeIndex}
                    />
                    <SearchContainer onSearchResultClick={handleSearchResultClicked} onMouseOver={handleMouseOverSearchResult} searchState={props.searchState} onSearch={handleScopeSearch} />
                    <ForeachLoopDisplay
                        id={props.id}
                        variableName={`#var.${props.elementParameters?.parameters["variable"]}`}
                        variableValue={variableValue}
                        expression={props.elementParameters?.parameters["expression"].value}
                        loopResult={foreachLoopResults ? props.currentIndexes && foreachLoopResults[props.currentIndexes[props.step?.stepId as string]] : null}
                    />
                </div>;
            }
            case ElementType.InclusiveDecisionBranch:
            case ElementType.ConditionBranch: {
                const expression = props.elementParameters?.parameters["expression"];
                return <div>
                    <ExpressionResultDisplay id={props.id} expressionResults={props.step?.result} parameterExpression={expression ? expression.value : null} />
                </div>;
            }
            case ElementType.Decision:
                return <div>
                    <ValueDisplay value={props.elementParameters?.parameters["expression"].value} isResult={false} label={FRENDS.Localizations.Expression} key={props.id + "_expression"} />
                    {props.step?.result ?
                        <ValueDisplay value={(props.step.result !== "else").toString()} isResult={false} label={FRENDS.Localizations.ExpressionResult} key={props.id + "_expressionResult"} />
                        : null}
                </div>;

            case ElementType.Expression:
                return <div>
                    <OptionalJsonDisplay result={props.step?.result} jsonDisplayType={JsonDisplayType.Result} />
                    {getParameters()}
                    {props.elementParameters?.parameters["variableName"] ? <ValueDisplay value={props.elementParameters.parameters["variableName"]} isResult={false} label={FRENDS.Localizations.Variable} key={props.id + "_variable"} /> : null}
                    <ValueDisplay value={props.elementParameters?.parameters["variableExpression"].value} isResult={false} label={FRENDS.Localizations.Expression} key={props.id + "_expression"} />
                </div>;

            case ElementType.CallActivity:
            case ElementType.Start:
                return <div>
                    {props.subprocessExecutionId ?
                        <S.LinkButton className="show-subprocess-execution-link" href={`ProcessInstance/Instance/${props.subprocessExecutionId}?environmentName=${props.environmentName}`} style={{ marginBottom: '18px' }}>
                            {FRENDS.Localizations.ShowSubprocessExecution}
                        </S.LinkButton>
                        : props.elementType === ElementType.CallActivity ? FRENDS.Localizations.Subprocess_NotLogged : null}
                    <OptionalJsonDisplay result={props.step?.result} jsonDisplayType={JsonDisplayType.Result} />
                    {getParameters()}
                    {getProcessVariables()}
                </div>;

            case ElementType.Task: {
                let taskNameDisplay: JSX.Element | null = null;
                let taskRetries: JSX.Element | null = null;
                if (props.taskDetails && props.elementParameters?.selectedTypeId) {
                    const taskDetails = props.taskDetails[props.elementParameters.selectedTypeId];
                    if (taskDetails) {
                        const taskName = `${taskDetails.displayName}, ${taskDetails.packageName}`;
                        taskNameDisplay = <ValueDisplay label={FRENDS.Localizations.Task} isResult={false} value={taskName} />;
                    }
                    if (props.step?.taskRetries) {
                        taskRetries = <ValueDisplay label={"Retries before finished"} isResult={false} value={props.step.taskRetries} />
                    }
                }
                return <div>
                    {taskRetries}
                    {taskNameDisplay}
                    <OptionalJsonDisplay jsonDisplayType={JsonDisplayType.Result} result={props.step?.result} />
                    {getParameters()}
                </div>;
            }
            default:
                return <div>
                    <OptionalJsonDisplay jsonDisplayType={JsonDisplayType.Result} result={props.step?.result} />
                    {getParameters()}
                </div>;

        }
    }

    return (
        <S.ParameterViewerStyled id={props.id + "_parameters"} key={props.id}>
            <ElementDisplayHeader
                name={props.stepName}
                state={props.step?.state}
                duration={props.step?.duration}
                startTime={props.step?.startTimeUtc}
                endTime={props.step?.endTimeUtc}
            />
            {renderPanel()}
        </S.ParameterViewerStyled>
    );
};
